import { Check } from 'lucide-react';

import { cn } from '@/lib/cn';

/**
 * Colores aproximados que evocan cada categoría WSET para mostrar dentro del
 * cáliz. No pretenden ser exactos sino reconocibles a primera vista en mobile.
 */
const COLOR_FILLS: Record<string, string> = {
  // Tintos
  purpura: '#4A1B5D',
  rubi: '#9A1B2A',
  granate: '#6E1A1F',
  teja: '#B95E3C',
  marron: '#5A3825',
  // Blancos
  verde_limon: '#DBE08C',
  amarillo_limon: '#E8D156',
  dorado: '#CFA231',
  ambar: '#B36C1F',
  marron_blanco: '#8C5A2B',
  // Rosados
  rosado: '#F4B1B5',
  salmon: '#F39574',
  naranja: '#EC8849',
};

interface ColorOption {
  value: string;
  label: string;
}

export interface ColorGroup {
  title: string;
  options: ColorOption[];
}

/**
 * Renderiza un grupo de copas para elegir color. Opcionalmente muestra todos
 * los grupos juntos (Blanco / Rosado / Tinto) con título separador.
 */
export function ColorGlassPicker({
  value,
  onChange,
  groups,
}: {
  value: string;
  onChange: (v: string) => void;
  groups: ColorGroup[];
}) {
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <div key={g.title}>
          <p className="text-[11px] uppercase tracking-wide text-notion-500 mb-1.5">
            {g.title}
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {g.options.map((o) => {
              const fill = COLOR_FILLS[o.value] ?? '#999';
              const active = value === o.value;
              return (
                <button
                  type="button"
                  key={o.value}
                  onClick={() => onChange(o.value)}
                  aria-pressed={active}
                  className={cn(
                    'relative flex flex-col items-center gap-1 py-2 px-1 rounded-2xl border-2 transition-all active:scale-[0.97]',
                    active
                      ? 'border-emerald-600 bg-emerald-50'
                      : 'border-notion-200 bg-white hover:border-notion-400',
                  )}
                >
                  {active && (
                    <Check size={14} className="absolute top-1.5 right-1.5 text-emerald-600" />
                  )}
                  <WineGlassBowl fill={fill} />
                  <span className="text-[11px] font-medium text-notion-700 text-center leading-tight">
                    {o.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Solo el cáliz/cuerpo de la copa, en zoom. Maximizamos el área del líquido
 * para que el color sea fácil de leer a primera vista en mobile.
 */
function WineGlassBowl({ fill }: { fill: string }) {
  return (
    <svg viewBox="0 0 50 50" width="48" height="48" aria-hidden="true">
      {/* Vidrio externo del cáliz (tipo tulipán). */}
      <path
        d="M 6 6 L 44 6 Q 46 28, 25 46 Q 4 28, 6 6 Z"
        fill="#FFFFFF"
        stroke="#B0B0B0"
        strokeWidth="1.5"
      />
      {/* Líquido coloreado (un poco más adentro). */}
      <path
        d="M 10 12 L 40 12 Q 42 27, 25 42 Q 8 27, 10 12 Z"
        fill={fill}
        opacity="0.93"
      />
      {/* Reflejo superior izquierdo. */}
      <ellipse cx="15" cy="20" rx="2.5" ry="6" fill="white" opacity="0.35" />
      {/* Brillo en el borde. */}
      <path
        d="M 6 6 L 44 6"
        stroke="#FFFFFF"
        strokeWidth="0.5"
        opacity="0.5"
      />
    </svg>
  );
}
